import { useMemo } from 'react';
import fixtures from '../data/fixtures.json';
import { stanceFor, sortByStance } from '../rules/stance';
import StanceBadge, { STANCE_LABEL } from '../components/StanceBadge';
import type { DemoStock, Stance, Thresholds } from '../rules/types';

const stocks = fixtures.stocks as DemoStock[];
const heldTickers = new Set(fixtures.positions.map((p) => p.ticker));

function pctStr(f: number, digits = 0) {
  return `${(f * 100).toFixed(digits)}%`;
}

export default function Today({
  thresholds, onOpenDeepDive,
}: {
  thresholds: Thresholds;
  onOpenDeepDive: (ticker: string) => void;
}) {
  const rows = useMemo(() => {
    const evaluated = stocks.map((s) => {
      const held = heldTickers.has(s.ticker);
      const { stance, rationale } = stanceFor(s, held, thresholds);
      return {
        stock: s, held, stance, rationale, ticker: s.ticker,
        dayChange: (s.price - s.previousClose) / s.previousClose,
      };
    });
    return sortByStance(evaluated) as typeof evaluated;
  }, [thresholds]);

  const tally = useMemo(() => {
    const c: Record<Stance, number> = { buy: 0, watch: 0, sell: 0, hold: 0 };
    for (const r of rows) c[r.stance]++;
    return c;
  }, [rows]);

  return (
    <>
      <div className="tally">
        {(['buy', 'watch', 'sell', 'hold'] as Stance[]).map((s) => (
          <span className="chip" key={s}>
            <b style={{ color: `var(--${s})` }}>{STANCE_LABEL[s]}</b> {tally[s]}
          </span>
        ))}
        <span className="chip">{stocks.length} instruments · {heldTickers.size} held</span>
      </div>

      <div className="rows">
        {rows.map((r) => (
          <button className="row rowButton" key={r.ticker} onClick={() => onOpenDeepDive(r.ticker)}>
            <StanceBadge stance={r.stance} />

            <div className="main">
              <div className="name">
                {r.stock.name}
                <span className="tk">{r.ticker}</span>
                {r.held && <span className="heldTag">held</span>}
              </div>
              <div className="why">{r.rationale}</div>
            </div>

            <div className="figs">
              <div className="px">{r.stock.price.toFixed(2)}</div>
              <div className={`chg ${r.dayChange >= 0 ? 'up' : 'down'}`}>
                {r.dayChange >= 0 ? '▲' : '▼'} {pctStr(Math.abs(r.dayChange), 2)}
              </div>
              <div className="meta">
                {pctStr((r.stock.price - r.stock.fiftyTwoWeekLow) / r.stock.fiftyTwoWeekLow)} above low
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
